import { StorageCollectionName } from '@livv/models';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Button } from '@mui/material';
import { getDownloadURL, getMetadata, ref as storageRef } from 'firebase/storage';
import useTranslation from 'next-translate/useTranslation';
import { FC, useEffect, useState } from 'react';
import { storage } from '@config/firebase/client';

interface CdcLetterPdfDownloadButtonProps {
    letterId: string;
    letterTitle: string;
}

interface CdcLetterPdfData {
    downloadUrl: string;
    fileName: string;
    letterId: string;
}

const CdcLetterPdfDownloadButton: FC<CdcLetterPdfDownloadButtonProps> = ({
    letterId,
    letterTitle,
}) => {
    const { t } = useTranslation('vogelActuCdcLetter');
    const [pdfData, setPdfData] = useState<CdcLetterPdfData | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const filePath = `${StorageCollectionName.CDC_LETTER_PDFS}/${letterId}.pdf`;

        const loadPdfData = async () => {
            setPdfData(null);

            try {
                const fileRef = storageRef(storage, filePath);
                const [metadata, downloadUrl] = await Promise.all([
                    getMetadata(fileRef),
                    getDownloadURL(fileRef),
                ]);

                if (isMounted) {
                    setPdfData({
                        downloadUrl,
                        fileName:
                            metadata.customMetadata?.originalFileName?.trim() ||
                            `${letterTitle}.pdf`,
                        letterId,
                    });
                }
            } catch {
                if (isMounted) {
                    setPdfData(null);
                }
            }
        };

        loadPdfData().catch(() => undefined);

        return () => {
            isMounted = false;
        };
    }, [letterId, letterTitle]);

    const downloadPdf = async () => {
        if (isDownloading || !pdfData || pdfData.letterId !== letterId) {
            return;
        }

        setIsDownloading(true);

        try {
            const response = await fetch(pdfData.downloadUrl);
            if (!response.ok) {
                throw new Error('Unable to download CDC letter PDF');
            }

            const localObjectUrl = URL.createObjectURL(await response.blob());
            const downloadLink = document.createElement('a');
            downloadLink.href = localObjectUrl;
            downloadLink.download = pdfData.fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(localObjectUrl);
        } catch (error) {
            console.error(error);
        } finally {
            setIsDownloading(false);
        }
    };

    if (!pdfData || pdfData.letterId !== letterId) {
        return null;
    }

    return (
        <Button
            disabled={isDownloading}
            onClick={downloadPdf}
            startIcon={<DownloadOutlinedIcon />}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            variant="contained"
        >
            {t('downloadPdf')}
        </Button>
    );
};

export default CdcLetterPdfDownloadButton;
